import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { postAnswer } from "../../api/answer";
import { getQuizById, getQuizIdsByScenario } from "../../api/quiz";
import iconCorrect from "../../assets/icon-correct.png";
import mano from "../../assets/mano.png";
import ScenarioBar from "../../components/ScenarioBar";
import { useTimer } from "../../hooks/useTimer";
import { updateScore, updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import {
	getScenarioByScreen,
	getTimer,
	updateStats,
} from "../../utils/helpers";
import {
	alertError,
	modalConfirm,
	modalQuestion1Feedback,
} from "../../utils/logger";

const QUIZ_ID = QUIZZES.SCENARIO1;
const SCENARIO_ID = SCENARIOS.SCENARIO_1;
const NAVIGATE_TO = ROUTES.TRANSITION_SCENARIOS;

const PREFIX_JUSTIFIED = '<div><div><strong>Esta prueba está justificada.</strong> He aquí los resultados:</div>';
const PREFIX_NOT_JUSTIFIED = '<div><div><strong>Esta prueba no está justificada.</strong></div>';

function getFullExplanationHtml(explanation, isJustified) {
	if (!explanation) return isJustified ? PREFIX_JUSTIFIED : PREFIX_NOT_JUSTIFIED;
	const s = explanation.trim();
	if (s.includes("Esta prueba está justificada") || s.includes("Esta prueba no está justificada")) return s;
	const prefix = isJustified ? PREFIX_JUSTIFIED : PREFIX_NOT_JUSTIFIED;
	return prefix + s;
}

function shuffleArray(arr) {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

function Scenario1({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const participant = useSelector((state) => state.group);
	const cachedQuiz = useSelector((state) => state.quizCache?.[SCENARIO_ID]);
	const scenarioNum = getScenarioByScreen(location.pathname);

	const [isCompleted, setIsCompleted] = useState(false);

	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -20;

	const [points, setPoints] = useState(0);
	const [penalty, setPenalty] = useState(0);
	const [resolvedQuizId, setResolvedQuizId] = useState(null);
	const [options, setOptions] = useState([]);
	const [quizLoading, setQuizLoading] = useState(true);

	const [time, setTime] = useState(0);

	useTimer(
		() => {
			setTime(time + 1);
		},
		isCompleted ? null : 1000
	);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) {
			setQuizLoading(false);
			return;
		}

		// Usar caché si se precargó desde la pantalla anterior
		if (cachedQuiz?.data?.options && Array.isArray(cachedQuiz.data.options) && cachedQuiz.data.options.length > 0) {
			setResolvedQuizId(cachedQuiz.resolvedQuizId || null);
			const mapped = cachedQuiz.data.options.map((o) => ({
				...o,
				selected: false,
				checked: false,
			}));
			setOptions(shuffleArray(mapped));
			setQuizLoading(false);
			return;
		}

		setQuizLoading(true);
		let cancelled = false;
		const params = { game: gameId };
		getQuizIdsByScenario(SCENARIO_ID, params)
			.then((listRes) => {
				if (cancelled) return;
				const list = listRes?.data && Array.isArray(listRes.data) ? listRes.data : [];
				const id = list[0]?._id || QUIZ_ID;
				setResolvedQuizId(id);
				return getQuizById(id);
			})
			.then((res) => {
				if (cancelled) return;
				if (res?.data?.options && Array.isArray(res.data.options) && res.data.options.length > 0) {
					const mapped = res.data.options.map((o) => ({
						...o,
						selected: false,
						checked: false,
					}));
					setOptions(shuffleArray(mapped));
				}
				setQuizLoading(false);
			})
			.catch(() => { if (!cancelled) setQuizLoading(false); });
		return () => { cancelled = true; };
	}, [participant?.game]);

	const SelectOption = ({ id }) => {
		let option = options.find((x) => x.id === id);

		function handleOptionClick() {
			if (isCompleted || option?.checked) return;

			let updatedOptions = options.map((x) =>
				x.id === id ? { ...x, selected: true } : { ...x }
			);

			setOptions(updatedOptions);

			modalConfirm({
				text:
					!option?.confirmExplanation &&
					"¿Confirmas que quieres solicitar esta prueba?",
				htmlText:
					option?.confirmExplanation && option?.confirmExplanation,
			}).then((res) => {
				if (res.isConfirmed) {
					let updatedOptions = options.map((x) =>
						x.id === id
							? { ...x, selected: true, checked: true }
							: { ...x }
					);

					setOptions(updatedOptions);

					modalQuestion1Feedback({
						question: option?.text,
						answerHtml: getFullExplanationHtml(option?.explanation, option?.correct),
						correct: option?.correct,
						points: option?.correct
							? CORRECT_ANSWER_POINTS
							: WRONG_ANSWER_POINTS,
					});

					if (option?.correct)
						setPoints(points + CORRECT_ANSWER_POINTS);
					else {
						setPenalty(penalty + WRONG_ANSWER_POINTS);
						setPoints(points + WRONG_ANSWER_POINTS);
					}

					if (!updatedOptions?.some((x) => x.correct && !x.selected))
						setIsCompleted(true);
				} else {
					let updatedOptions = options.map((x) =>
						x.id === id ? { ...x, selected: false } : { ...x }
					);

					setOptions(updatedOptions);
				}
			});
		}

		function getBackgroundColor() {
			return option?.selected && option?.correct && option?.checked
				? "#D6D431"
				: option?.selected && !option?.correct && option?.checked
				? "#FF5948"
				: option?.selected
				? "#EAF886"
				: "#FFFFFF";
		}

		return (
			<div>
				<div
					className={`d-flex align-items-center justify-content-center txt-turquoise text-center mt-3 ${
						option?.selected ? "bold option-selected" : ""
					}`}
					style={{
						position: "relative",
						height: "15vh",
						borderRadius: "10px",
						backgroundColor: getBackgroundColor(),
					}}
					onClick={handleOptionClick}
				>
					{option?.text}
					{option?.checked && option?.correct && (
						<img
							style={{ position: "absolute", top: 10, right: 10 }}
							width={20}
							src={iconCorrect}
						/>
					)}
				</div>
			</div>
		);
	};

	const correctCount = options?.filter((o) => o.correct).length ?? 0;

	async function submitAnswer() {
		if (options?.some((x) => x.correct && !x.selected)) {
			alertError({
				error: " ",
				customMessage: `Para avanzar, debes escoger las ${correctCount} pruebas justificadas.`,
			});
			return;
		}

		let totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: resolvedQuizId ?? QUIZ_ID,
			group: participant?._id,
			score: points,
			time: time,
			correct: true,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(updateScore(points, SCORE_TYPES.SCORE));
					dispatch(updateScore(penalty, SCORE_TYPES.PENALTY));

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + points,
						penalty: participant?.penalty + penalty,
						lastVisited: NAVIGATE_TO,
					});
					navigate(NAVIGATE_TO, {
						state: { activeScenario: scenarioNum + 1 },
					});
				}
			})
			.catch((error) => {});
	}

	if (quizLoading) {
		return (
			<div className="h-100 bg-prueba d-flex align-items-center justify-content-center">
				<div className="txtWhite">Cargando quiz...</div>
				<ScenarioBar activeScenario={scenarioNum} />
			</div>
		);
	}

	if (!options?.length) {
		return (
			<div className="h-100 bg-prueba d-flex align-items-center justify-content-center">
				<div className="txtWhite">No hay opciones de quiz configuradas.</div>
				<ScenarioBar activeScenario={scenarioNum} />
			</div>
		);
	}

	return (
		<div className="h-100 bg-prueba">
			<div className="container">
				<div className="row h-100">
					<div className="col-12">
						<div className="d-flex justify-content-center align-items-center">
							<img src={mano} alt="" />
							<div className="ms-2 txtWhite">
								Has seleccionado{" "}
								{
									options?.filter(
										(x) =>
											x.selected && x.correct && x.checked
									)?.length
								}{" "}
								de {correctCount} pruebas
							</div>
						</div>
					</div>
					{options.map((option) => {
						return (
							<div key={option.id} className="col-4">
								<SelectOption id={option.id} />
							</div>
						);
					})}
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					bottom: "13vh",
					zIndex: 1000,
				}}
			>
				<div className="d-flex justify-content-end">
					<div className="btn-main" onClick={() => submitAnswer()}>
						<div className="text-btn-main">AVANZAR</div>
					</div>
				</div>
			</div>
			<ScenarioBar activeScenario={scenarioNum}></ScenarioBar>
		</div>
	);
}

export default injectIntl(Scenario1);
